import React from 'react'
import { UseFormRegister } from 'react-hook-form'
import './styles.css'
import { AsteriskIcon } from 'lucide-react'
import clsx from 'clsx'
import Text from '../Text'
import { AnimatePresence, motion } from 'motion/react'

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  name: string
  label: string
  register: UseFormRegister<any> // eslint-disable-line @typescript-eslint/no-explicit-any
  validationIconStatus?: boolean
  error?: string
  showErrorDropdown?: boolean
}

export default function TextFormField({
  name,
  label,
  register,
  validationIconStatus,
  children,
  prefix,
  error,
  showErrorDropdown: showError,
  ...inputHtmlProps
}: Props) {
  const {
    onChange: rhfOnChange,
    onBlur: rhfOnBlur,
    name: rhfName,
    ref: rhfRef,
  } = register(name)

  return (
    <div>
      <div
        className={clsx(
          `form-field-root !flex flex-row gap-1 !h-[60px]`,
          inputHtmlProps.disabled
            ? `bg-base-grey-100 cursor-not-allowed opacity-50`
            : '',
          error ? '!border-themed-error' : '',
          inputHtmlProps.className,
        )}
      >
        <div className="flex flex-col gap-1 w-full">
          <label
            htmlFor={name}
            className="text-themed-grey-500 font-bold select-none pointer-events-none bg-transparent top-2 text-sm"
          >
            <div className="flex flex-row">
              {label}
              {inputHtmlProps.required && (
                <AsteriskIcon className="text-themed-error" size={12} />
              )}
            </div>
          </label>
          <div className="flex flex-row items-center">
            <div className="text-sm text-themed-grey-400">{prefix}</div>
            <input
              {...inputHtmlProps} // keep all parent-provided props (like placeholder, className, etc.)
              type={inputHtmlProps.type || 'text'}
              name={rhfName}
              ref={rhfRef}
              onChange={(e) => {
                inputHtmlProps.onChange?.(e) // your custom handler from parent
                rhfOnChange(e) // react-hook-form's handler
              }}
              onBlur={(e) => {
                inputHtmlProps.onBlur?.(e)
                rhfOnBlur(e)
              }}
              className={clsx(
                'h-full w-full bg-opacity-0 border-none outline-none self-end text-sm bg-transparent text-themed-grey-500 placeholder:text-themed-grey-400',
                inputHtmlProps.className,
              )}
              placeholder={inputHtmlProps.placeholder ?? ''}
            />
          </div>
        </div>
        {children}
      </div>
      {
        <AnimatePresence initial={false}>
          {showError && error && (
            <motion.div
              key={`${name}-error`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{ overflow: 'hidden' }}
            >
              <Text variant="body4" className="text-themed-error py-2">
                <div className="flex flex-row gap-1">
                  <span>{error}</span>
                </div>
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
      }
    </div>
  )
}

type FormIconProps = {
  children?: React.ReactNode
}

export function TextFormFieldIcon({ children }: FormIconProps) {
  return (
    <div className="flex justify-center select-none pointer-events-none items-center">
      {children}
    </div>
  )
}

TextFormFieldIcon.displayName = 'TextFormIcon'
